import { useState, useEffect } from 'react';
import { Sparkles, X, ChevronRight } from 'lucide-react';
import { useAuth } from '@/App';

/**
 * WingmanNudge — Proactive contextual alert that appears when
 * analysis data contains concerning patterns. Clicking it scrolls
 * to the WingmanInsight component or opens the Wingman chat.
 *
 * Props:
 *   patterns  – array of { id, label, severity } objects
 *              severity: 'warning' | 'alert' | 'info'
 */
export default function WingmanNudge({ patterns = [] }) {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (patterns.length === 0) { setVisible(false); return; }
    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, [patterns]);

  if (dismissed || !visible || patterns.length === 0) return null;

  const highestSeverity = patterns.some(p => p.severity === 'alert')
    ? 'alert'
    : patterns.some(p => p.severity === 'warning')
      ? 'warning'
      : 'info';

  const borderColor = highestSeverity === 'alert' ? 'border-red-300' : highestSeverity === 'warning' ? 'border-amber-300' : 'border-teal-300';
  const bgColor = highestSeverity === 'alert' ? 'bg-red-50' : highestSeverity === 'warning' ? 'bg-amber-50' : 'bg-teal-50';
  const iconColor = highestSeverity === 'alert' ? 'text-red-500' : highestSeverity === 'warning' ? 'text-amber-500' : 'text-teal-500';
  const headerColor = highestSeverity === 'alert' ? 'text-red-800' : highestSeverity === 'warning' ? 'text-amber-800' : 'text-teal-800';

  const scrollToInsight = () => {
    const el = document.querySelector('[data-testid="wingman-insight"]');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        const btn = el.querySelector('[data-testid="wingman-insight-toggle"]');
        if (btn) btn.click();
      }, 600);
    }
    setDismissed(true);
  };

  return (
    <div
      className={`mt-4 rounded-lg border ${borderColor} ${bgColor} px-4 py-3 animate-in fade-in slide-in-from-top-2 duration-500`}
      data-testid="wingman-nudge"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          <Sparkles className={`h-4 w-4 mt-0.5 shrink-0 ${iconColor}`} />
          <div className="min-w-0">
            <p className={`text-xs font-semibold ${headerColor}`}>
              Wingman noticed {patterns.length === 1 ? 'something' : `${patterns.length} things`} in your results
            </p>
            <ul className="mt-1.5 space-y-1">
              {patterns.map(p => (
                <li key={p.id} className="flex items-center gap-1.5 text-xs text-slate-600">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
                    p.severity === 'alert' ? 'bg-red-400' : p.severity === 'warning' ? 'bg-amber-400' : 'bg-teal-400'
                  }`} />
                  {p.label}
                </li>
              ))}
            </ul>
            <button
              onClick={scrollToInsight}
              className={`mt-2 inline-flex items-center gap-1 text-xs font-medium ${iconColor} hover:underline`}
              data-testid="wingman-nudge-action"
            >
              Ask Wingman to explain <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-slate-400 hover:text-slate-600 shrink-0"
          data-testid="wingman-nudge-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Helper: Build pattern list from growth model analysis data
 */
export function detectGrowthModelPatterns({ missingFishIntelligence, calculatedBestFitAdjustment, result }) {
  const patterns = [];
  if (!result) return patterns;

  if (missingFishIntelligence?.ssot?.hasMissingFish) {
    const strengthScore = missingFishIntelligence?.ssot?.detectionStrengthScore || 0;
    const strengthLabel = missingFishIntelligence?.ssot?.detectionStrength || 'Low';
    const actionability = missingFishIntelligence?.ssot?.actionability
      || missingFishIntelligence?.decisionContext?.decisionClass
      || null;
    // ★ SSOT — canonical range label + strength-gated wording.
    //   Only HIGH confidence may use "signal detected" (per H3).
    const rangeLabel = missingFishIntelligence?.ssot?.missingFishRangeLabel || null;
    const isIndicationOnly = strengthScore < 40
      || strengthLabel === 'Low'
      || strengthLabel === 'Indication Only'
      || actionability === 'MONITOR_ONLY';
    if (isIndicationOnly) {
      patterns.push({
        id: 'missing-fish',
        label: 'Indicative deviation observed — insufficient data for confirmation',
        severity: 'info'
      });
    } else if (strengthScore >= 70) {
      patterns.push({
        id: 'missing-fish',
        label: rangeLabel
          ? `Missing fish signal detected — ${rangeLabel}`
          : 'Missing fish signal detected',
        severity: 'alert'
      });
    } else {
      // Moderate — canonical "deviation pattern" wording, never "signal detected".
      patterns.push({
        id: 'missing-fish',
        label: rangeLabel
          ? `Deviation pattern observed — ${rangeLabel}`
          : 'Deviation pattern observed — validate with field count',
        severity: 'warning'
      });
    }
  }

  if (calculatedBestFitAdjustment && Math.abs(calculatedBestFitAdjustment.percent) > 20) {
    patterns.push({
      id: 'model-deviation',
      label: `Growth deviates ${Math.abs(calculatedBestFitAdjustment.percent).toFixed(0)}% from standard model (adjusted to ${Math.round((1 + calculatedBestFitAdjustment.percent / 100) * 100)}%)`,
      severity: 'warning'
    });
  }

  if (missingFishIntelligence?.trajectoryJump?.jump_detected) {
    patterns.push({
      id: 'trajectory-jump',
      label: 'Trajectory jump detected — possible mortality event',
      severity: 'alert'
    });
  }

  if (missingFishIntelligence?.dataQuality?.overallScore && missingFishIntelligence.dataQuality.overallScore < 50) {
    patterns.push({
      id: 'low-data-quality',
      label: `Data quality score is low (${missingFishIntelligence.dataQuality.overallScore}%)`,
      severity: 'warning'
    });
  }

  return patterns;
}

/**
 * Helper: Build pattern list from spreadsheet analysis data
 */
export function detectSpreadsheetPatterns({ missingFishIntelligence, activeRecord }) {
  const patterns = [];
  if (!activeRecord) return patterns;

  if (missingFishIntelligence?.ssot?.hasMissingFish) {
    const strengthScore = missingFishIntelligence?.ssot?.detectionStrengthScore || 0;
    const strengthLabel = missingFishIntelligence?.ssot?.detectionStrength || 'Low';
    const actionability = missingFishIntelligence?.ssot?.actionability
      || missingFishIntelligence?.decisionContext?.decisionClass
      || null;
    const rangeLabel = missingFishIntelligence?.ssot?.missingFishRangeLabel || null;
    const isIndicationOnly = strengthScore < 40
      || strengthLabel === 'Low'
      || strengthLabel === 'Indication Only'
      || actionability === 'MONITOR_ONLY';
    if (isIndicationOnly) {
      patterns.push({
        id: 'missing-fish',
        label: 'Indicative deviation observed — insufficient data for confirmation',
        severity: 'info'
      });
    } else if (strengthScore >= 70) {
      patterns.push({
        id: 'missing-fish',
        label: rangeLabel
          ? `Missing fish signal detected — ${rangeLabel}`
          : 'Missing fish signal detected',
        severity: 'alert'
      });
    } else {
      patterns.push({
        id: 'missing-fish',
        label: rangeLabel
          ? `Deviation pattern observed — ${rangeLabel}`
          : 'Deviation pattern observed — validate with field count',
        severity: 'warning'
      });
    }
  }

  // ★ Feeding alert — aligned with SSOT feeding classification.
  // Only emit an overfeeding alert when overfeeding is DOMINANT
  // (feedingState === 'Overfeeding') or ECONOMICALLY SIGNIFICANT during a
  // Mixed pattern (waste share ≥ 5%). Prevents contradictions like
  // "Mixed feeding (RFI ~90%)" being paired with a loud "Overfeeding risk" nudge.
  const ssotFeeding = missingFishIntelligence?.ssot || {};
  const ssotFeedingState = ssotFeeding.feedingState;
  const isWasteSig = !!ssotFeeding.isWasteEconomicallySignificant;
  const riskLevel = missingFishIntelligence?.overfeedingRisk?.level;
  const hasRiskSignal = riskLevel && riskLevel !== 'Low';
  const dominantOverfeeding = ssotFeedingState === 'Overfeeding';
  const significantMixed = ssotFeedingState === 'Mixed' && isWasteSig;

  if (hasRiskSignal && dominantOverfeeding) {
    patterns.push({
      id: 'overfeeding',
      label: `Overfeeding (dominant): RFI ${ssotFeeding.rfi}% — feed allocation exceeds corrected recommendation`,
      severity: riskLevel === 'High' ? 'alert' : 'warning'
    });
  } else if (hasRiskSignal && significantMixed) {
    patterns.push({
      id: 'overfeeding',
      label: `Mixed feeding pattern (RFI ${ssotFeeding.rfi}%): ${ssotFeeding.wastedFeedKg?.toLocaleString() || '?'} kg of period-level overfeeding (~${ssotFeeding.wasteSharePct || '?'}% of total feed) — economically significant`,
      severity: 'warning'
    });
  }

  if (missingFishIntelligence?.trajectoryJump?.jump_detected) {
    patterns.push({
      id: 'trajectory-jump',
      label: 'Trajectory jump detected — possible mortality event',
      severity: 'alert'
    });
  }

  if (missingFishIntelligence?.dataQuality?.overallScore && missingFishIntelligence.dataQuality.overallScore < 50) {
    patterns.push({
      id: 'low-data-quality',
      label: `Data quality score is low (${missingFishIntelligence.dataQuality.overallScore}%)`,
      severity: 'warning'
    });
  }

  return patterns;
}

/**
 * Helper: Build pattern list from prognosis data
 */
export function detectPrognosisPatterns({ result, missingFishIntelligence }) {
  const patterns = [];
  if (!result) return patterns;

  if (result.days_to_target > 365) {
    patterns.push({
      id: 'long-cycle',
      label: `Projected cycle is ${(result.days_to_target / 30).toFixed(0)} months — unusually long`,
      severity: 'warning'
    });
  }

  if (missingFishIntelligence?.trajectoryJump?.jump_detected) {
    patterns.push({
      id: 'trajectory-jump',
      label: 'Trajectory jump detected — forecast may be affected',
      severity: 'alert'
    });
  }

  if (result.mortality_rate_percent && result.mortality_rate_percent > 15) {
    patterns.push({
      id: 'high-mortality',
      label: `Projected mortality rate is high (${result.mortality_rate_percent}%)`,
      severity: 'warning'
    });
  }

  return patterns;
}

/**
 * Helper: Build pattern list from SFR result
 */
export function detectSFRPatterns({ result }) {
  const patterns = [];
  if (!result) return patterns;

  if (result.sfr_percentage && result.sfr_percentage > 3.5) {
    patterns.push({
      id: 'high-sfr',
      label: `SFR of ${result.sfr_percentage}% is above typical range — verify temperature and weight`,
      severity: 'info'
    });
  }

  if (result.sfr_percentage && result.sfr_percentage < 0.5) {
    patterns.push({
      id: 'low-sfr',
      label: `SFR of ${result.sfr_percentage}% is very low — fish may be approaching harvest weight`,
      severity: 'info'
    });
  }

  return patterns;
}
